import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Settings as SettingsIcon, Users, Mail, Trash2, Plus, Send, CheckCircle, AlertCircle, Edit, UserPlus, Building, FileCheck, Upload, Download, Lock, Unlock, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { API, errorText } from '@/utils/api';

// Available regions
const REGIONS = ["Jharkhand", "Bihar", "West Bengal", "Odisha", "Assam", "Tripura", "Meghalaya"];

// Available divisions
const DIVISIONS = ["Arvind Tresca DTR", "Arvind Tresca WS", "GG DTR", "GG WS", "TAS"];

// ── Fuzzy trade-name de-dup helpers (Untagged Labels) ──────────────
// Normalise for comparison: lowercase, strip punctuation, collapse
// whitespace. So "Mix-Dispatch" and "mix dispatch" compare equal.
const _normTrade = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const _levenshtein = (a, b) => {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
};

// Returns the closest existing trade name that is similar-but-not-
// identical to `draft`, or null. Catches three kinds of near-dupes:
//   1) word-order variants ("Mix Shirting Dispatch" vs "Shirting Mix
//      Dispatch") and pure case/punctuation differences,
//   2) high word-overlap (Jaccard ≥ 0.6),
//   3) typos (Levenshtein similarity ≥ 0.82).
const findSimilarTradeName = (draft, suggestions) => {
  const raw = (draft || '').trim();
  if (raw.length < 3 || !Array.isArray(suggestions)) return null;
  if (suggestions.some((s) => s === raw)) return null; // exact reuse → fine
  const d = _normTrade(raw);
  if (!d) return null;
  const dWords = d.split(' ').filter(Boolean);
  const dSorted = [...dWords].sort().join(' ');
  const dSet = new Set(dWords);
  let best = null;
  let bestScore = 0;
  for (const s of suggestions) {
    const n = _normTrade(s);
    if (!n) continue;
    if (n === d) return s; // same after normalising (case/punct) → suggest canonical
    const nWords = n.split(' ').filter(Boolean);
    if ([...nWords].sort().join(' ') === dSorted) return s; // word-order variant
    const nSet = new Set(nWords);
    let inter = 0;
    dSet.forEach((w) => { if (nSet.has(w)) inter += 1; });
    const jac = inter / (dSet.size + nSet.size - inter);
    const lev = 1 - _levenshtein(d, n) / Math.max(d.length, n.length);
    const score = Math.max(jac, lev >= 0.82 ? lev : 0);
    if (score > bestScore) { bestScore = score; best = s; }
  }
  return bestScore >= 0.6 ? best : null;
};

// Role labels and colors
const ROLE_CONFIG = {
  admin: { label: 'Administrator', color: 'bg-red-100 text-red-800 border-red-200' },
  accounts: { label: 'Accounts', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  sales_member: { label: 'Sales Member', color: 'bg-gray-100 text-gray-800 border-gray-200' }
};

// CD Policy options (calculated on PRE-TAX/TAXABLE value)
const CD_POLICIES = {
  'cd_5_15_3_30': { label: '5% in 15 days / 3% in 30 days', rate: 5.0, rate2: 3.0, days: 15, days2: 30, tiered: true },
  'cd_4_15': { label: '4% CD in 15 days', rate: 4.0, days: 15 },
  'cd_3_30': { label: '3% CD in 30 days', rate: 3.0, days: 30 },
  'cd_3_15': { label: '3% CD in 15 days', rate: 3.0, days: 15 },
  'cd_2_30': { label: '2% CD in 30 days', rate: 2.0, days: 30 },
  'no_cd': { label: 'No CD', rate: 0, days: 0 }
};

export default function Settings() {
  const { user, selectedDivision } = useOutletContext();
  // Deep links (e.g. Product Mix → "Fabric rules"): ?tab=trade_rules&division=…&q=…
  const [searchParams] = useSearchParams();
  const linkTab = searchParams.get('tab');
  const linkDivision = searchParams.get('division');
  const linkSearch = searchParams.get('q');
  
  // Employee management state
  const [employees, setEmployees] = useState([]);
  const [availableDivisions, setAvailableDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    division: '',
    regions: [],
    divisions: [],
    role: 'sales_member',
    can_import_orders: false,
    import_orders_scope: 'own',
  });
  
  // User management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [activeTab, setActiveTab] = useState(() => (linkTab === 'trade_rules' ? 'trade_rules' : 'employees'));

  // Order places config (editable "place of order" + company order-taker lists)
  const [orderPlaces, setOrderPlaces] = useState([]);
  const [companyTakers, setCompanyTakers] = useState([]);
  const [loadingOrderPlaces, setLoadingOrderPlaces] = useState(false);
  const [savingOrderPlaces, setSavingOrderPlaces] = useState(false);

  // Format locks state (admin-only import format protection)
  const [formatLocks, setFormatLocks] = useState([]);
  const [formatLocksLoading, setFormatLocksLoading] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(null); // upload_type currently uploading

  // Trade fabric category rules state (admin/accounts)
  // Opens on the division in the link, else the sidebar's division filter
  // (it used to open on TAS whatever division you were working in).
  const [tradeRulesDivision, setTradeRulesDivision] = useState(() =>
    [linkDivision, selectedDivision].find(d => DIVISIONS.includes(d)) || 'TAS');
  const [tradeRulesScope, setTradeRulesScope] = useState('global'); // 'global' | 'per_division'
  const [tradeRulesList, setTradeRulesList] = useState([]);
  const [tradeRulesCoverage, setTradeRulesCoverage] = useState([]);
  const [tradeRulesLoading, setTradeRulesLoading] = useState(false);
  const [tradeRulesFilter, setTradeRulesFilter] = useState('all'); // 'all' | 'unset' | 'set' | 'default_fallback'
  const [tradeRulesSearch, setTradeRulesSearch] = useState(linkSearch || '');
  const [savingRuleTrade, setSavingRuleTrade] = useState(null);
  // Quality-name merges ('Lot 1 Aspro' + 'Lot 2 Aspro' → 'Aspro'), applied app-wide.
  const [tradeMerges, setTradeMerges] = useState([]);
  const [mergePick, setMergePick] = useState([]);          // canonical names ticked in the table
  const [mergeDialog, setMergeDialog] = useState(null);    // { variants: [...] } while open
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeScope, setMergeScope] = useState('division');
  const [mergeSaving, setMergeSaving] = useState(false);

  // Stores (ship-to) back-fill (admin). Re-reads the retained dispatch and
  // order files and stamps ship_to_code / ship_to_name on rows that have
  // none, seeding the store master. Dry run first; Apply is confirmed.
  const [shipToDivision, setShipToDivision] = useState('TAS');
  const [shipToRunning, setShipToRunning] = useState(null); // 'dry' | 'apply' | null
  const [shipToResult, setShipToResult] = useState(null);   // last response + whether it was a dry run

  // Untagged Labels (sort-label → trade_name) state
  const [labelRulesDivision, setLabelRulesDivision] = useState('all');
  const [labelRules, setLabelRules] = useState([]);
  const [unmappedLabels, setUnmappedLabels] = useState([]);
  const [tradeNameSuggestions, setTradeNameSuggestions] = useState([]);
  const [labelRulesLoading, setLabelRulesLoading] = useState(false);
  const [savingLabel, setSavingLabel] = useState(null);
  const [applyingLabelRules, setApplyingLabelRules] = useState(false);
  // Pending trade_name typed against each unmapped label, keyed by
  // `${division}|||${label}`
  const [labelDrafts, setLabelDrafts] = useState({});
  // In-place edits of existing rules' trade_name, keyed by rule_id
  const [ruleEdits, setRuleEdits] = useState({});

  // CD Policy state
  const [cdPolicies, setCdPolicies] = useState(null);
  const [loadingCdPolicies, setLoadingCdPolicies] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  // Division CD Rates state
  const [divisionCdRates, setDivisionCdRates] = useState([]);
  const [loadingDivisionCdRates, setLoadingDivisionCdRates] = useState(false);
  const [editingDivisionRate, setEditingDivisionRate] = useState(null);
  const [divisionRateForm, setDivisionRateForm] = useState({
    high_rate: 5,
    high_days: 15,
    low_rate: 3,
    low_days: 30
  });
  
  // Division+Region CD Rates state
  const [divisionRegionCdRates, setDivisionRegionCdRates] = useState([]);
  const [editingDivisionRegionRate, setEditingDivisionRegionRate] = useState(null); // {division, region}
  
  // Customer-specific CD Rates state
  const [customerCdRates, setCustomerCdRates] = useState([]);
  const [loadingCustomerCdRates, setLoadingCustomerCdRates] = useState(false);
  const [addingCustomerCdRate, setAddingCustomerCdRate] = useState(false);
  const [editingCustomerCdRate, setEditingCustomerCdRate] = useState(null);
  const [customerCdRateForm, setCustomerCdRateForm] = useState({
    customer_id: '',
    high_rate: 5,
    high_days: 15,
    low_rate: 3,
    low_days: 30,
    reason: ''
  });
  
  // Reminder state
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [minDaysOverdue, setMinDaysOverdue] = useState(1);
  const [headOfficeEmail, setHeadOfficeEmail] = useState(user?.email || '');
  
  // Performance state
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  useEffect(() => {
    // Accounts can reach Settings only for the classification tabs
    // (Fabric Rules / Untagged Labels). Skip the admin-only data
    // fetches for them so they don't 403-spam, and land them on a tab
    // they can actually use instead of the admin "Employees" default.
    if (user?.role === 'accounts') {
      if (linkTab !== 'trade_rules') setActiveTab('untagged_labels');
      return;
    }
    fetchEmployees();
    fetchPerformanceSummary();
    fetchUsers();
    fetchCdPolicies();
    fetchCustomers();
    fetchDivisionCdRates();
    fetchCustomerCdRates();
    if (user?.role === 'admin') fetchFormatLocks();
  }, [user?.role]);

  // ── Trade Fabric Category Rules ─────────────────────────────────
  // Loads two things in parallel:
  //   - All existing rules for the selected division
  //   - Last-90-day dispatched-trade coverage (per-trade dispatch
  //     volume + current rule status) so admin can see which trades
  //     still need a rule
  const fetchTradeRulesData = async (division) => {
    if (!division || division === 'all') return;
    setTradeRulesLoading(true);
    try {
      // Load ALL rules (global + this division). The coverage
      // endpoint already merges global + per-division rules into
      // current_rule_fabric, but for the inline scope toggle we
      // need both lists separately.
      const [rulesAllRes, covRes, mergesRes] = await Promise.all([
        axios.get(`${API}/trade-category-rules`, { withCredentials: true }),
        axios.get(`${API}/trade-category-rules/coverage`, { params: { division }, withCredentials: true }),
        axios.get(`${API}/trade-merges`, { params: { division }, withCredentials: true }),
      ]);
      setTradeMerges(mergesRes.data?.merges || []);
      setMergePick([]);
      const allRules = rulesAllRes.data?.rules || [];
      // Keep just the global + this division's rules
      setTradeRulesList(allRules.filter(r => r.division === '*' || r.division === division));
      setTradeRulesCoverage(covRes.data?.trades || []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error('Failed to load trade rules');
    } finally {
      setTradeRulesLoading(false);
    }
  };

  // Upsert a rule. fabric=null clears the existing rule.
  // scope='global' writes a global ('*') rule; scope='per_division'
  // writes a rule for the currently-selected division (overrides
  // global for that division).
  const saveTradeRule = async (trade_name, fabric) => {
    setSavingRuleTrade(trade_name);
    const rule_division = tradeRulesScope === 'global' ? '*' : tradeRulesDivision;
    try {
      if (fabric === null) {
        const existing = tradeRulesList.find(r =>
          (r.trade_name || '').toLowerCase() === (trade_name || '').toLowerCase()
          && r.division === rule_division
        );
        if (existing?.rule_id) {
          await axios.delete(`${API}/trade-category-rules/${existing.rule_id}`, { withCredentials: true });
          toast.success(`Cleared ${tradeRulesScope === 'global' ? 'global' : tradeRulesDivision} rule for ${trade_name}`);
        }
      } else {
        await axios.post(`${API}/trade-category-rules`, {
          division: rule_division, trade_name, fabric,
        }, { withCredentials: true });
        toast.success(`${trade_name} → ${fabric}${rule_division === '*' ? ' (global)' : ` (${tradeRulesDivision} only)`}`);
      }
      await fetchTradeRulesData(tradeRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save rule');
    } finally {
      setSavingRuleTrade(null);
    }
  };

  const openMergeDialog = (variants) => {
    setMergeDialog({ variants });
    setMergeTarget('');
    setMergeScope('division');
  };

  const saveMerge = async () => {
    const target = mergeTarget.trim();
    if (!target) { toast.error('Type or pick the name to merge into'); return; }
    setMergeSaving(true);
    const division = mergeScope === 'global' ? '*' : tradeRulesDivision;
    try {
      for (const variant of mergeDialog.variants) {
        if (variant.toLowerCase() === target.toLowerCase()) continue;
        await axios.post(`${API}/trade-merges`, { division, variant, target }, { withCredentials: true });
      }
      toast.success(`${mergeDialog.variants.join(', ')} → ${target}${division === '*' ? ' (all divisions)' : ` (${tradeRulesDivision})`}`);
      setMergeDialog(null);
      await fetchTradeRulesData(tradeRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Merge failed');
    } finally {
      setMergeSaving(false);
    }
  };

  const undoMerge = async (m) => {
    if (!window.confirm(`Undo: show "${m.variant}" on its own again (no longer under "${m.target}")?`)) return;
    try {
      await axios.delete(`${API}/trade-merges/${m.merge_id}`, { withCredentials: true });
      toast.success(`${m.variant} shows on its own again`);
      await fetchTradeRulesData(tradeRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Undo failed');
    }
  };

  const runBackfillDispatchCategory = async () => {
    if (!window.confirm(`Backfill dispatch.category for ${tradeRulesDivision} from order data? Safe to re-run; manual overrides are preserved.`)) return;
    try {
      const res = await axios.post(`${API}/admin/backfill-dispatch-category`, null, {
        params: { division: tradeRulesDivision }, withCredentials: true,
      });
      toast.success(`Backfilled ${res.data?.updated} dispatches (${res.data?.skipped_manual} manual locks preserved)`);
      await fetchTradeRulesData(tradeRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Backfill failed');
    }
  };

  // Read-only simulation of a Re-Run with store-aware matching on vs off.
  const [matchPreview, setMatchPreview] = useState(null);
  const [matchPreviewRunning, setMatchPreviewRunning] = useState(false);
  const runMatchPreview = async () => {
    setMatchPreviewRunning(true);
    try {
      const res = await axios.get(`${API}/admin/preview-store-matching`, {
        params: { division: shipToDivision }, withCredentials: true, timeout: 600000 });
      setMatchPreview(res.data);
    } catch (err) {
      toast.error(errorText(err) || 'Preview failed');
    } finally {
      setMatchPreviewRunning(false);
    }
  };

  const runBackfillShipTo = async (dryRun) => {
    if (!dryRun && !window.confirm(`Back-fill ship-to (store) codes on ${shipToDivision} dispatches and orders from the retained upload files?\n\nOnly rows without a store are touched; later files win on conflicts; the store master is seeded. Run a dry run first if you have not.`)) return;
    setShipToRunning(dryRun ? 'dry' : 'apply');
    try {
      // One request per file type (dispatches, then orders): each reads every
      // retained file of that type, so two shorter requests stay well inside
      // the server's request limit. Totals are summed here.
      const parts = {};
      const totals = {};
      for (const ft of ['dispatches', 'orders']) {
        const res = await axios.post(`${API}/admin/backfill-ship-to`,
          { division: shipToDivision, file_type: ft, dry_run: dryRun },
          { withCredentials: true, timeout: 600000 });
        Object.assign(parts, res.data?.parts || {});
        Object.entries(res.data?.totals || {}).forEach(([k, v]) => {
          if (typeof v === 'number') totals[k] = (totals[k] || 0) + v;
        });
        setShipToResult({ dry_run: dryRun, division: shipToDivision, totals: { ...totals }, parts: { ...parts }, partial: ft === 'dispatches' });
      }
      setShipToResult({ dry_run: dryRun, division: shipToDivision, totals, parts, partial: false });
      toast.success(dryRun ? 'Dry run complete — nothing written' : 'Ship-to back-fill applied');
    } catch (err) {
      toast.error(errorText(err) || 'Ship-to back-fill failed');
    } finally {
      setShipToRunning(null);
    }
  };

  const runSeedTradeRules = async () => {
    if (!window.confirm('Seed the table with the legacy hardcoded overrides (Ranger/Langston/Vyff/Gemstone2/Eureka:15602 for TAS)? Idempotent; existing rules are untouched.')) return;
    try {
      const res = await axios.post(`${API}/admin/seed-trade-category-rules`, null, { withCredentials: true });
      toast.success(`Seeded ${res.data?.seeded} rules (${res.data?.skipped_existing} already existed)`);
      await fetchTradeRulesData(tradeRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Seed failed');
    }
  };

  useEffect(() => {
    if (activeTab === 'trade_rules' && (user?.role === 'admin' || user?.role === 'accounts')) {
      fetchTradeRulesData(tradeRulesDivision);
    }
  }, [activeTab, tradeRulesDivision, user?.role]);

  // ── Untagged Labels (sort-label → trade_name) ──────────────────
  const fetchLabelRulesData = async (division) => {
    setLabelRulesLoading(true);
    try {
      const params = (division && division !== 'all') ? { division } : {};
      const [rulesRes, unmappedRes, suggRes] = await Promise.all([
        axios.get(`${API}/trade-name-rules`, { params, withCredentials: true }),
        axios.get(`${API}/trade-name-rules/unmapped`, { params, withCredentials: true }),
        axios.get(`${API}/trade-name-rules/suggestions`, { withCredentials: true }),
      ]);
      setLabelRules(rulesRes.data?.rules || []);
      setUnmappedLabels(unmappedRes.data?.labels || []);
      setTradeNameSuggestions(suggRes.data?.trade_names || []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error('Failed to load untagged labels');
    } finally {
      setLabelRulesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'untagged_labels' && (user?.role === 'admin' || user?.role === 'accounts')) {
      fetchLabelRulesData(labelRulesDivision);
    }
  }, [activeTab, labelRulesDivision, user?.role]);

  useEffect(() => {
    if (activeTab === 'order_places' && (user?.role === 'admin' || user?.role === 'accounts')) {
      fetchOrderPlaces();
    }
  }, [activeTab, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save a sort-label → trade_name rule. division '' means use the
  // group's own division; '*' would be global.
  const saveLabelRule = async (division, sort_label, trade_name) => {
    const tn = (trade_name || '').trim();
    if (!tn) { toast.error('Enter a trade name first'); return; }
    const key = `${division}|||${sort_label}`;
    setSavingLabel(key);
    try {
      await axios.post(`${API}/trade-name-rules`, {
        division: division || '*', sort_label, trade_name: tn,
      }, { withCredentials: true });
      toast.success(`"${sort_label}" → ${tn}`);
      setLabelDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
      await fetchLabelRulesData(labelRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save rule');
    } finally {
      setSavingLabel(null);
    }
  };

  // Rename an existing rule's trade_name. Re-posts the same
  // (division, label) so it upserts in place — only the trade_name
  // changes. Used to make spellings uniform (e.g. "Suiting Mix Bale"
  // vs "Mix Suiting Bale" entered before the autocomplete existed).
  const saveRuleEdit = async (rule) => {
    const tn = (ruleEdits[rule.rule_id] ?? rule.trade_name ?? '').trim();
    if (!tn) { toast.error('Trade name cannot be empty'); return; }
    if (tn === (rule.trade_name || '').trim()) return; // unchanged
    setSavingLabel(rule.rule_id);
    try {
      const res = await axios.post(`${API}/trade-name-rules`, {
        division: rule.division,
        sort_label: rule.sort_label_display || rule.sort_label,
        trade_name: tn,
      }, { withCredentials: true });
      const synced = res.data?.synced_dispatches || 0;
      toast.success(`Renamed to ${tn}${synced ? ` · ${synced} dispatches updated` : ''}`);
      setRuleEdits(prev => { const n = { ...prev }; delete n[rule.rule_id]; return n; });
      await fetchLabelRulesData(labelRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to rename rule');
    } finally {
      setSavingLabel(null);
    }
  };

  const deleteLabelRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.sort_label_display || rule.sort_label}" → ${rule.trade_name}?\n\nDispatches already named by it keep their trade_name; only future re-runs stop applying it.`)) return;
    setSavingLabel(rule.rule_id);
    try {
      await axios.delete(`${API}/trade-name-rules/${rule.rule_id}`, { withCredentials: true });
      toast.success('Rule deleted');
      await fetchLabelRulesData(labelRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to delete rule');
    } finally {
      setSavingLabel(null);
    }
  };

  // Apply all rules to existing dispatches via the trade-name backfill.
  const applyLabelRules = async () => {
    if (!window.confirm('Apply all label rules to existing dispatches now?\n\nThis writes the trade name onto matching dispatch rows (also resolves product-code sorts via sister/order/prefix). Safe to re-run.')) return;
    setApplyingLabelRules(true);
    try {
      const res = await axios.post(`${API}/admin/backfill-trade-name-from-sort`, null, { withCredentials: true });
      const p = res.data?.passes || {};
      toast.success(`Applied: ${res.data?.resolved || 0} dispatches named (${p.rule || 0} by your rules)`);
      await fetchLabelRulesData(labelRulesDivision);
    } catch (err) {
      toast.error(errorText(err) || 'Apply failed');
    } finally {
      setApplyingLabelRules(false);
    }
  };

  const fetchFormatLocks = async () => {
    setFormatLocksLoading(true);
    try {
      const res = await axios.get(`${API}/admin/format-locks`, { withCredentials: true });
      setFormatLocks(res.data?.locks || []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error('Failed to load format locks');
    } finally {
      setFormatLocksLoading(false);
    }
  };

  const handleUploadReference = async (uploadType, file) => {
    if (!file) return;
    setUploadingRef(uploadType);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/format-locks/${uploadType}/reference`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'Reference set');
      fetchFormatLocks();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to set reference');
    } finally {
      setUploadingRef(null);
    }
  };

  const handleDownloadReference = async (uploadType, filename) => {
    try {
      const res = await axios.get(`${API}/admin/format-locks/${uploadType}/download`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${uploadType}_reference.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleUnlockFormat = async (uploadType, label) => {
    if (!window.confirm(`Unlock ${label}?\n\nAll future imports of this type will be accepted without format validation until a new reference is uploaded.`)) return;
    try {
      await axios.delete(`${API}/admin/format-locks/${uploadType}`, { withCredentials: true });
      toast.success(`${label} unlocked`);
      fetchFormatLocks();
    } catch (err) {
      toast.error('Unlock failed');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      // 403 = role-gated panel, deliberately silent; anything else the editor
      // must know about — an empty list here can be re-saved over real data.
      if (error?.response?.status !== 403) toast.error('Could not load customers — refresh before editing.');
    }
  };
  
  const fetchOrderPlaces = async () => {
    setLoadingOrderPlaces(true);
    try {
      const response = await axios.get(`${API}/config/order-places`, { withCredentials: true });
      setOrderPlaces(response.data?.order_places || []);
      setCompanyTakers(response.data?.company_takers || []);
    } catch (error) {
      console.error('Failed to fetch order places:', error);
      if (error?.response?.status !== 403) toast.error('Could not load order settings.');
    } finally {
      setLoadingOrderPlaces(false);
    }
  };

  const handleSaveOrderPlaces = async () => {
    const cleaned = orderPlaces.map(p => (p || '').trim()).filter(Boolean);
    if (cleaned.length === 0) { toast.error('Add at least one place'); return; }
    const cleanedTakers = companyTakers.map(p => (p || '').trim()).filter(Boolean);
    setSavingOrderPlaces(true);
    try {
      const res = await axios.post(`${API}/config/order-places`,
        { order_places: cleaned, company_takers: cleanedTakers }, { withCredentials: true });
      setOrderPlaces(res.data?.order_places || cleaned);
      setCompanyTakers(res.data?.company_takers || cleanedTakers);
      toast.success('Order settings saved');
    } catch (error) {
      toast.error(errorText(error) || 'Failed to save order settings');
    } finally {
      setSavingOrderPlaces(false);
    }
  };

  const fetchCdPolicies = async () => {
    setLoadingCdPolicies(true);
    try {
      const response = await axios.get(`${API}/config/cd-policies`, { withCredentials: true });
      setCdPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch CD policies:', error);
      if (error?.response?.status !== 403) toast.error('Could not load CD policies — refresh before editing.');
    } finally {
      setLoadingCdPolicies(false);
    }
  };
  
  const fetchDivisionCdRates = async () => {
    setLoadingDivisionCdRates(true);
    try {
      const response = await axios.get(`${API}/config/division-cd-rates`, { withCredentials: true });
      setDivisionCdRates(response.data.division_cd_rates || []);
      setDivisionRegionCdRates(response.data.division_region_cd_rates || []);
    } catch (error) {
      console.error('Failed to fetch division CD rates:', error);
      if (error?.response?.status !== 403) toast.error('Could not load division CD rates — refresh before editing.');
    } finally {
      setLoadingDivisionCdRates(false);
    }
  };
  
  const handleEditDivisionRate = (division, region = null) => {
    if (region) {
      // Editing division+region specific rate
      const rate = divisionRegionCdRates.find(r => r.division === division && r.region === region);
      if (rate) {
        setDivisionRateForm({
          high_rate: rate.high_rate || 5,
          high_days: rate.high_days || 15,
          low_rate: rate.low_rate || 3,
          low_days: rate.low_days || 30
        });
      } else {
        // Start with division default or system default
        const divRate = divisionCdRates.find(r => r.division === division);
        setDivisionRateForm({
          high_rate: divRate?.high_rate || 5,
          high_days: divRate?.high_days || 15,
          low_rate: divRate?.low_rate || 3,
          low_days: divRate?.low_days || 30
        });
      }
      setEditingDivisionRegionRate({ division, region });
      setEditingDivisionRate(null);
    } else {
      // Editing division default rate
      const rate = divisionCdRates.find(r => r.division === division);
      if (rate) {
        setDivisionRateForm({
          high_rate: rate.high_rate || 5,
          high_days: rate.high_days || 15,
          low_rate: rate.low_rate || 3,
          low_days: rate.low_days || 30
        });
      } else {
        setDivisionRateForm({
          high_rate: 5,
          high_days: 15,
          low_rate: 3,
          low_days: 30
        });
      }
      setEditingDivisionRate(division);
      setEditingDivisionRegionRate(null);
    }
  };
  
  const handleSaveDivisionRate = async () => {
    try {
      if (editingDivisionRegionRate) {
        // Save division+region specific rate
        const { division, region } = editingDivisionRegionRate;
        await axios.post(
          `${API}/config/division-cd-rates?division=${encodeURIComponent(division)}&region=${encodeURIComponent(region)}&high_rate=${divisionRateForm.high_rate}&high_days=${divisionRateForm.high_days}&low_rate=${divisionRateForm.low_rate}&low_days=${divisionRateForm.low_days}`,
          {},
          { withCredentials: true }
        );
        toast.success(`CD rates updated for ${division} + ${region}`);
        setEditingDivisionRegionRate(null);
      } else if (editingDivisionRate) {
        // Save division default rate
        await axios.post(
          `${API}/config/division-cd-rates?division=${encodeURIComponent(editingDivisionRate)}&high_rate=${divisionRateForm.high_rate}&high_days=${divisionRateForm.high_days}&low_rate=${divisionRateForm.low_rate}&low_days=${divisionRateForm.low_days}`,
          {},
          { withCredentials: true }
        );
        toast.success(`CD rates updated for ${editingDivisionRate}`);
        setEditingDivisionRate(null);
      }
      fetchDivisionCdRates();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to save CD rates');
    }
  };
  
  const handleDeleteDivisionRegionRate = async (division, region) => {
    try {
      await axios.delete(
        `${API}/config/division-cd-rates?division=${encodeURIComponent(division)}&region=${encodeURIComponent(region)}`,
        { withCredentials: true }
      );
      toast.success(`Custom rate removed for ${division} + ${region}. Will use division default.`);
      fetchDivisionCdRates();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to remove custom rate');
    }
  };
  
  // Customer CD Rates functions
  const fetchCustomerCdRates = async () => {
    setLoadingCustomerCdRates(true);
    try {
      const response = await axios.get(`${API}/config/customer-cd-rates`, { withCredentials: true });
      setCustomerCdRates(response.data.customer_cd_rates || []);
    } catch (error) {
      console.error('Failed to fetch customer CD rates:', error);
      if (error?.response?.status !== 403) toast.error('Could not load customer CD rates — refresh before editing.');
    } finally {
      setLoadingCustomerCdRates(false);
    }
  };
  
  const handleAddCustomerCdRate = () => {
    setCustomerCdRateForm({
      customer_id: '',
      high_rate: 5,
      high_days: 15,
      low_rate: 3,
      low_days: 30,
      reason: ''
    });
    setAddingCustomerCdRate(true);
    setEditingCustomerCdRate(null);
  };
  
  const handleEditCustomerCdRate = (rate) => {
    setCustomerCdRateForm({
      customer_id: rate.customer_id,
      high_rate: rate.high_rate || 5,
      high_days: rate.high_days || 15,
      low_rate: rate.low_rate || 3,
      low_days: rate.low_days || 30,
      reason: rate.reason || ''
    });
    setEditingCustomerCdRate(rate.customer_id);
    setAddingCustomerCdRate(false);
  };
  
  const handleSaveCustomerCdRate = async () => {
    if (!customerCdRateForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    try {
      await axios.post(
        `${API}/config/customer-cd-rates?customer_id=${encodeURIComponent(customerCdRateForm.customer_id)}&high_rate=${customerCdRateForm.high_rate}&high_days=${customerCdRateForm.high_days}&low_rate=${customerCdRateForm.low_rate}&low_days=${customerCdRateForm.low_days}&reason=${encodeURIComponent(customerCdRateForm.reason || '')}`,
        {},
        { withCredentials: true }
      );
      toast.success('Customer CD rate saved');
      setAddingCustomerCdRate(false);
      setEditingCustomerCdRate(null);
      fetchCustomerCdRates();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to save customer CD rate');
    }
  };
  
  const handleDeleteCustomerCdRate = async (customerId) => {
    try {
      await axios.delete(
        `${API}/config/customer-cd-rates/${encodeURIComponent(customerId)}`,
        { withCredentials: true }
      );
      toast.success('Custom CD rate removed. Customer will use division/region defaults.');
      fetchCustomerCdRates();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to remove custom rate');
    }
  };
  
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        withCredentials: true
      });
      setEmployees(response.data.employees || []);
      setAvailableDivisions(response.data.available_divisions || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data.users || []);
    } catch (error) {
      // Non-admin users won't have access (403) — keep that silent.
      console.error('Failed to fetch users:', error);
      if (error?.response?.status !== 403) toast.error('Could not load users — refresh before changing roles.');
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`, {}, {
        withCredentials: true
      });
      toast.success(`User role updated to ${ROLE_CONFIG[newRole]?.label || newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to update role');
    }
  };
  
  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`, {
        withCredentials: true
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to delete user');
    }
  };
  
  const fetchPerformanceSummary = async () => {
    setLoadingPerformance(true);
    try {
      const response = await axios.get(`${API}/employees/performance-summary`, {
        withCredentials: true
      });
      setPerformanceSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch performance:', error);
      if (error?.response?.status !== 403) toast.error('Could not load performance summary.');
    } finally {
      setLoadingPerformance(false);
    }
  };
  
  const openEmployeeDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      // Handle both old (division/region) and new (divisions/regions) format
      const divisions = employee.divisions || (employee.division ? [employee.division] : []);
      const regions = employee.regions || (employee.region ? [employee.region] : []);
      setEmployeeForm({
        name: employee.name,
        email: employee.email,
        phone: employee.phone || '',
        regions: regions,
        divisions: divisions,
        role: employee.role || 'sales_member',
        can_import_orders: employee.can_import_orders || false,
        import_orders_scope: employee.import_orders_scope || 'own',
      });
    } else {
      setEditingEmployee(null);
      setEmployeeForm({
        name: '',
        email: '',
        phone: '',
        regions: [],
        divisions: [],
        role: 'sales_member',
        can_import_orders: false,
        import_orders_scope: 'own',
      });
    }
    setEmployeeDialogOpen(true);
  };
  
  const handleSaveEmployee = async () => {
    if (!employeeForm.name || !employeeForm.email || employeeForm.regions.length === 0 || employeeForm.divisions.length === 0) {
      toast.error('Please fill all required fields (including at least one region and division)');
      return;
    }
    
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.employee_id}`, employeeForm, {
          withCredentials: true
        });
        toast.success(`Employee ${employeeForm.name} updated`);
      } else {
        await axios.post(`${API}/employees`, employeeForm, {
          withCredentials: true
        });
        toast.success(`Employee ${employeeForm.name} created`);
      }
      setEmployeeDialogOpen(false);
      fetchEmployees();
      fetchPerformanceSummary();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to save employee');
    }
  };
  
  const toggleDivision = (division) => {
    setEmployeeForm(prev => {
      const current = prev.divisions || [];
      if (current.includes(division)) {
        return { ...prev, divisions: current.filter(d => d !== division) };
      } else {
        return { ...prev, divisions: [...current, division] };
      }
    });
  };
  
  const toggleRegion = (region) => {
    setEmployeeForm(prev => {
      const current = prev.regions || [];
      if (current.includes(region)) {
        return { ...prev, regions: current.filter(r => r !== region) };
      } else {
        return { ...prev, regions: [...current, region] };
      }
    });
  };
  
  const selectAllDivisions = () => {
    setEmployeeForm(prev => ({
      ...prev,
      divisions: [...availableDivisions]
    }));
  };
  
  const selectAllRegions = () => {
    setEmployeeForm(prev => ({
      ...prev,
      regions: [...REGIONS]
    }));
  };
  
  const clearAllDivisions = () => {
    setEmployeeForm(prev => ({
      ...prev,
      divisions: []
    }));
  };
  
  const clearAllRegions = () => {
    setEmployeeForm(prev => ({
      ...prev,
      regions: []
    }));
  };
  
  const handleDeleteEmployee = async (employee) => {
    if (!confirm(`Deactivate ${employee.name}? This will also unassign them from all customers.`)) return;
    
    try {
      await axios.delete(`${API}/employees/${employee.employee_id}`, {
        withCredentials: true
      });
      toast.success(`Employee ${employee.name} deactivated`);
      fetchEmployees();
      fetchPerformanceSummary();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to delete employee');
    }
  };
  
  const handleBulkAssign = async () => {
    if (!confirm('Auto-assign unassigned customers to employees based on region matching?')) return;
    
    try {
      const response = await axios.post(`${API}/customers/bulk-assign-employees`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchPerformanceSummary();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to auto-assign');
    }
  };
  
  const handleSendSmartReminders = async () => {
    if (!headOfficeEmail) {
      toast.error('Please enter Head Office email');
      return;
    }
    
    setSendingReminders(true);
    setReminderResult(null);
    
    try {
      const response = await axios.post(`${API}/reminders/send-smart`, {
        min_days_overdue: minDaysOverdue,
        head_office_email: headOfficeEmail
      }, {
        withCredentials: true
      });
      
      setReminderResult(response.data);
      if (response.data.emails_sent > 0) {
        toast.success(`${response.data.emails_sent} reminder email(s) sent!`);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(errorText(error) || 'Failed to send reminders');
      setReminderResult({ status: 'error', message: errorText(error) || 'Failed to send reminders' });
    } finally {
      setSendingReminders(false);
    }
  };
  
  if (user?.role !== 'admin' && user?.role !== 'accounts') {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-red-800">You don&apos;t have permission to access settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground mb-1 flex items-center gap-2">
          <SettingsIcon className="text-muted-foreground" size={28} />
          Settings
        </h1>
        <p className="text-muted-foreground text-sm">Manage users, employees, customer assignments, and reminders</p>
      </div>
      
      {/* Tabs for different settings sections */}
      <div className="flex gap-2 border-b pb-2 flex-wrap">
        {user?.role === 'admin' && (
          <Button
            variant={activeTab === 'employees' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('employees')}
            className="gap-2"
          >
            <Building size={16} />
            Employees
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            variant={activeTab === 'users' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('users')}
            className="gap-2"
          >
            <Users size={16} />
            User Access
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            variant={activeTab === 'cd_policies' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('cd_policies')}
            className="gap-2"
          >
            <CheckCircle size={16} />
            CD Policies
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            variant={activeTab === 'reminders' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('reminders')}
            className="gap-2"
          >
            <Mail size={16} />
            Reminders
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            variant={activeTab === 'format_locks' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('format_locks')}
            className="gap-2"
          >
            <FileCheck size={16} />
            Format Locks
          </Button>
        )}
        {(user?.role === 'admin' || user?.role === 'accounts') && (
          <Button
            variant={activeTab === 'trade_rules' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('trade_rules')}
            className="gap-2"
          >
            <FileCheck size={16} />
            Fabric Rules
          </Button>
        )}
        {(user?.role === 'admin' || user?.role === 'accounts') && (
          <Button
            variant={activeTab === 'untagged_labels' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('untagged_labels')}
            className="gap-2"
          >
            <FileCheck size={16} />
            Untagged Labels
          </Button>
        )}
        {(user?.role === 'admin' || user?.role === 'accounts') && (
          <Button
            variant={activeTab === 'order_places' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('order_places')}
            className="gap-2"
          >
            <ClipboardList size={16} />
            Order Setup
          </Button>
        )}
      </div>

      {/* Order Setup Tab — editable lists used on the order-upload form */}
      {activeTab === 'order_places' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Order Setup</CardTitle>
            <p className="text-sm text-muted-foreground">
              Options shown when uploading an order copy. Edit, add, or remove entries, then Save.
            </p>
          </CardHeader>
          <CardContent>
            {loadingOrderPlaces ? (
              <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Place of order</p>
                  {orderPlaces.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={p}
                        onChange={(e) => setOrderPlaces(prev => prev.map((x, i) => i === idx ? e.target.value : x))}
                        placeholder="Place name" />
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                        onClick={() => setOrderPlaces(prev => prev.filter((_, i) => i !== idx))}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setOrderPlaces(prev => [...prev, ''])}>
                    <Plus size={14} className="mr-1" /> Add place
                  </Button>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Company / principal order-takers</p>
                  <p className="text-[11px] text-muted-foreground -mt-1">For orders booked by an Arvind / Reliance employee (no agency staff present).</p>
                  {companyTakers.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={p}
                        onChange={(e) => setCompanyTakers(prev => prev.map((x, i) => i === idx ? e.target.value : x))}
                        placeholder="e.g. Arvind Employee" />
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                        onClick={() => setCompanyTakers(prev => prev.filter((_, i) => i !== idx))}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setCompanyTakers(prev => [...prev, ''])}>
                    <Plus size={14} className="mr-1" /> Add company taker
                  </Button>
                </div>
                <div className="md:col-span-2">
                  <Button size="sm" onClick={handleSaveOrderPlaces} disabled={savingOrderPlaces}>
                    {savingOrderPlaces ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* CD Policies Tab */}
      {activeTab === 'cd_policies' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              Cash Discount (CD) Rates by Division
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure CD rates for each division. Each division has two tiers: a higher rate for faster payment and a lower rate for extended payment.
            </p>
          </CardHeader>
          <CardContent>
            {loadingDivisionCdRates ? (
              <p className="text-sm text-muted-foreground">Loading CD rates...</p>
            ) : (
              <div className="space-y-4">
                {/* Division CD Rates Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border p-3 text-left text-sm font-medium">Division</th>
                        <th className="border p-3 text-center text-sm font-medium">High Rate</th>
                        <th className="border p-3 text-center text-sm font-medium">High Days</th>
                        <th className="border p-3 text-center text-sm font-medium">Low Rate</th>
                        <th className="border p-3 text-center text-sm font-medium">Low Days</th>
                        <th className="border p-3 text-center text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DIVISIONS.map(division => {
                        const rate = divisionCdRates.find(r => r.division === division);
                        const highRate = rate?.high_rate ?? 5;
                        const highDays = rate?.high_days ?? 15;
                        const lowRate = rate?.low_rate ?? 3;
                        const lowDays = rate?.low_days ?? 30;
                        const isDefault = rate?.is_default;
                        
                        return (
                          <tr key={division} className="hover:bg-slate-50">
                            <td className="border p-3 font-medium text-sm bg-slate-50">
                              {division}
                              {isDefault && (
                                <span className="ml-2 text-xs text-amber-600">(default)</span>
                              )}
                            </td>
                            <td className="border p-3 text-center">
                              <span className="font-semibold text-green-700">{highRate}%</span>
                            </td>
                            <td className="border p-3 text-center text-sm text-muted-foreground">
                              {highDays} days
                            </td>
                            <td className="border p-3 text-center">
                              <span className="font-semibold text-blue-700">{lowRate}%</span>
                            </td>
                            <td className="border p-3 text-center text-sm text-muted-foreground">
                              {lowDays} days
                            </td>
                            <td className="border p-3 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditDivisionRate(division)}
                                className="gap-1"
                              >
                                <Edit size={14} />
                                Edit
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Division + Region Specific Rates */}
                <div className="mt-8">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    Region-Specific Overrides (Division + Region)
                  </h4>
                  <p className="text-xs text-muted-foreground mb-4">
                    Set different CD rates for specific regions within a division. These override the division defaults above.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="border p-2 text-left font-medium">Division / Region</th>
                          {REGIONS.map(region => (
                            <th key={region} className="border p-2 text-center font-medium min-w-[120px]">
                              {region}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DIVISIONS.map(division => {
                          const divRate = divisionCdRates.find(r => r.division === division);
                          const divHighRate = divRate?.high_rate ?? 5;
                          const divLowRate = divRate?.low_rate ?? 3;
                          
                          return (
                            <tr key={division} className="hover:bg-slate-50">
                              <td className="border p-2 font-medium bg-slate-50">{division}</td>
                              {REGIONS.map(region => {
                                const regionRate = divisionRegionCdRates.find(
                                  r => r.division === division && r.region === region
                                );
                                
                                return (
                                  <td key={`${division}-${region}`} className="border p-2 text-center">
                                    {regionRate ? (
                                      <div className="space-y-1">
                                        <p className="text-xs">
                                          <span className="text-green-700 font-semibold">{regionRate.high_rate}%</span>
                                          <span className="text-muted-foreground">/{regionRate.high_days}d</span>
                                          {' | '}
                                          <span className="text-blue-700 font-semibold">{regionRate.low_rate}%</span>
                                          <span className="text-muted-foreground">/{regionRate.low_days}d</span>
                                        </p>
                                        <div className="flex gap-1 justify-center">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditDivisionRate(division, region)}
                                            className="h-6 px-2 text-xs"
                                          >
                                            <Edit size={12} />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteDivisionRegionRate(division, region)}
                                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                                          >
                                            <Trash2 size={12} />
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditDivisionRate(division, region)}
                                        className="h-7 px-2 text-xs text-muted-foreground"
                                      >
                                        <span className="text-gray-400">{divHighRate}%/{divLowRate}%</span>
                                        <Plus size={12} className="ml-1" />
                                      </Button>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Click on a cell to set custom rates for that division+region. Gray values show division defaults.
                  </p>
                </div>
                
                {/* Customer-Specific CD Rates Section */}
                <div className="mt-8">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                    Customer-Specific Exemptions (Highest Priority)
                  </h4>
                  <p className="text-xs text-muted-foreground mb-4">
                    Set custom CD rates for specific customers. These override both division and region rates.
                  </p>
                  
                  {/* Add New Customer CD Rate Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddCustomerCdRate}
                    className="mb-4 gap-1"
                  >
                    <Plus size={14} />
                    Add Customer Exemption
                  </Button>
                  
                  {/* Existing Customer CD Rates */}
                  {loadingCustomerCdRates ? (
                    <p className="text-sm text-muted-foreground">Loading customer exemptions...</p>
                  ) : customerCdRates.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-orange-50">
                            <th className="border p-2 text-left font-medium">Customer</th>
                            <th className="border p-2 text-center font-medium">Region</th>
                            <th className="border p-2 text-center font-medium">High Rate</th>
                            <th className="border p-2 text-center font-medium">Low Rate</th>
                            <th className="border p-2 text-left font-medium">Reason</th>
                            <th className="border p-2 text-center font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerCdRates.map(rate => (
                            <tr key={rate.customer_id} className="hover:bg-slate-50">
                              <td className="border p-2 font-medium">{rate.customer_name}</td>
                              <td className="border p-2 text-center text-muted-foreground">{rate.customer_region || '-'}</td>
                              <td className="border p-2 text-center">
                                <span className="text-green-700 font-semibold">{rate.high_rate}%</span>
                                <span className="text-muted-foreground text-xs">/{rate.high_days}d</span>
                              </td>
                              <td className="border p-2 text-center">
                                <span className="text-blue-700 font-semibold">{rate.low_rate}%</span>
                                <span className="text-muted-foreground text-xs">/{rate.low_days}d</span>
                              </td>
                              <td className="border p-2 text-xs text-muted-foreground max-w-[200px] truncate">
                                {rate.reason || '-'}
                              </td>
                              <td className="border p-2 text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditCustomerCdRate(rate)}
                                    className="h-7 px-2"
                                  >
                                    <Edit size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCustomerCdRate(rate.customer_id)}
                                    className="h-7 px-2 text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground p-4 bg-slate-50 rounded-lg">
                      No customer-specific CD rates configured. All customers use division/region rates.
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mt-6">
                  <p className="text-sm text-amber-800">
                    <strong>How CD is applied (priority order):</strong>
                  </p>
                  <ol className="text-sm text-amber-800 mt-1 list-decimal list-inside">
                    <li>Customer-specific rate (if set)</li>
                    <li>Division + Region rate (if set)</li>
                    <li>Division default rate</li>
                  </ol>
                  <p className="text-sm text-amber-800 mt-2">
                    <strong>Important:</strong> CD is always calculated on the <u>pre-tax/taxable value</u> (excluding GST), not on the total invoice amount.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Add/Edit Customer CD Rate Dialog */}
      <Dialog open={addingCustomerCdRate || !!editingCustomerCdRate} onOpenChange={(open) => {
        if (!open) {
          setAddingCustomerCdRate(false);
          setEditingCustomerCdRate(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingCustomerCdRate ? 'Edit Customer CD Rate' : 'Add Customer CD Exemption'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Customer Selection (only for new) */}
            {!editingCustomerCdRate && (
              <div>
                <Label className="text-sm font-medium">Select Customer</Label>
                <Select
                  value={customerCdRateForm.customer_id}
                  onValueChange={(value) => setCustomerCdRateForm({...customerCdRateForm, customer_id: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const allC = customers
                        .filter(c => !customerCdRates.find(r => r.customer_id === c.customer_id))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                      const groups = [
                        { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                        { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                        { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                        { label: 'S — Z', test: n => n >= 'S' },
                      ];
                      return groups.map(g => {
                        const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                        if (!items.length) return null;
                        return (
                          <SelectGroup key={g.label}>
                            <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                            {items.map(c => (
                              <SelectItem key={c.customer_id} value={c.customer_id}>
                                {c.name} {c.region ? `(${c.region})` : ''}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        );
                      });
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {editingCustomerCdRate && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm font-medium">
                  Customer: {customerCdRates.find(r => r.customer_id === editingCustomerCdRate)?.customer_name}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">High Rate (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={customerCdRateForm.high_rate}
                  onChange={(e) => setCustomerCdRateForm({...customerCdRateForm, high_rate: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">High Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={customerCdRateForm.high_days}
                  onChange={(e) => setCustomerCdRateForm({...customerCdRateForm, high_days: parseInt(e.target.value) || 15})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Low Rate (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={customerCdRateForm.low_rate}
                  onChange={(e) => setCustomerCdRateForm({...customerCdRateForm, low_rate: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Low Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={customerCdRateForm.low_days}
                  onChange={(e) => setCustomerCdRateForm({...customerCdRateForm, low_days: parseInt(e.target.value) || 30})}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Reason for Exemption (optional)</Label>
              <Input
                type="text"
                placeholder="E.g., Key account, Special agreement, etc."
                value={customerCdRateForm.reason}
                onChange={(e) => setCustomerCdRateForm({...customerCdRateForm, reason: e.target.value})}
                className="mt-1"
              />
            </div>
            
            <div className="p-3 bg-slate-100 rounded-lg text-sm">
              <p className="font-medium mb-1">Preview:</p>
              <p className="text-muted-foreground">
                {customerCdRateForm.high_rate}% CD for payment within {customerCdRateForm.high_days} days
              </p>
              <p className="text-muted-foreground">
                {customerCdRateForm.low_rate}% CD for payment within {customerCdRateForm.low_days} days
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddingCustomerCdRate(false); setEditingCustomerCdRate(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustomerCdRate} className="bg-orange-600 hover:bg-orange-700">
              Save Customer Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Division CD Rate Dialog */}
      <Dialog open={!!editingDivisionRate || !!editingDivisionRegionRate} onOpenChange={(open) => {
        if (!open) {
          setEditingDivisionRate(null);
          setEditingDivisionRegionRate(null);
        }
      }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>
              Edit CD Rates for {editingDivisionRegionRate 
                ? `${editingDivisionRegionRate.division} + ${editingDivisionRegionRate.region}` 
                : editingDivisionRate}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">High Rate (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={divisionRateForm.high_rate}
                  onChange={(e) => setDivisionRateForm({...divisionRateForm, high_rate: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">E.g., 5 for 5% CD</p>
              </div>
              <div>
                <Label className="text-sm font-medium">High Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={divisionRateForm.high_days}
                  onChange={(e) => setDivisionRateForm({...divisionRateForm, high_days: parseInt(e.target.value) || 15})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Payment within X days</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Low Rate (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={divisionRateForm.low_rate}
                  onChange={(e) => setDivisionRateForm({...divisionRateForm, low_rate: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">E.g., 3 for 3% CD</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Low Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={divisionRateForm.low_days}
                  onChange={(e) => setDivisionRateForm({...divisionRateForm, low_days: parseInt(e.target.value) || 30})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Payment within X days</p>
              </div>
            </div>
            
            <div className="p-3 bg-slate-100 rounded-lg text-sm">
              <p className="font-medium mb-1">Preview:</p>
              <p className="text-muted-foreground">
                {divisionRateForm.high_rate}% CD for payment within {divisionRateForm.high_days} days
              </p>
              <p className="text-muted-foreground">
                {divisionRateForm.low_rate}% CD for payment within {divisionRateForm.low_days} days
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDivisionRate(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDivisionRate} className="bg-green-600 hover:bg-green-700">
              Save Rates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* User Access Management Tab */}
      {activeTab === 'users' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <Users className="text-blue-600" size={20} />
              User Access Management
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Control who can access your app and what they can do
            </p>
          </CardHeader>
          <CardContent>
            {/* Permissions Legend */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium text-sm mb-3">Role Permissions:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-red-700">Administrator</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>• Full access to all features</li>
                    <li>• Reset data & manage users</li>
                    <li>• Edit/delete all records</li>
                  </ul>
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-blue-700">Accounts</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>• Record payments</li>
                    <li>• Edit invoices & credits</li>
                    <li>• Import data</li>
                  </ul>
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-700">Sales Member</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>• View-only access</li>
                    <li>• See assigned customers</li>
                    <li>• No edit permissions</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Users List */}
            {loadingUsers ? (
              <p className="text-sm text-muted-foreground">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div 
                    key={u.user_id} 
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      u.user_id === user.user_id ? 'bg-blue-50 border-blue-200' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users size={20} className="text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">
                          {u.name}
                          {u.user_id === user.user_id && (
                            <span className="ml-2 text-xs text-blue-600">(You)</span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleUpdateUserRole(u.user_id, value)}
                        disabled={u.user_id === user.user_id}
                      >
                        <SelectTrigger className={`w-40 h-9 text-sm border ${ROLE_CONFIG[u.role]?.color || ''}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">
                            <span className="text-red-700 font-medium">Administrator</span>
                          </SelectItem>
                          <SelectItem value="accounts">
                            <span className="text-blue-700 font-medium">Accounts</span>
                          </SelectItem>
                          <SelectItem value="sales_member">
                            <span className="text-gray-700 font-medium">Sales Member</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {u.user_id !== user.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(u.user_id, u.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> New users who sign in with Google will automatically get &quot;Sales Member&quot; role. 
                You can upgrade their access here after they sign in.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Employee Performance Overview - Only show on employees tab */}
      {activeTab === 'employees' && performanceSummary && (
        <Card className="border-border/60 shadow-sm bg-gradient-to-br from-slate-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <Users className="text-indigo-600" size={20} />
              Team Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold text-foreground">{performanceSummary.totals?.total_employees || 0}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Assigned Customers</p>
                <p className="text-2xl font-bold text-blue-600">{performanceSummary.totals?.assigned_customers || 0}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Head Office Customers</p>
                <p className="text-2xl font-bold text-purple-600">{performanceSummary.totals?.unassigned_customers || 0}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-bold text-foreground">{performanceSummary.totals?.total_customers || 0}</p>
              </div>
            </div>
            
            {/* Employee Performance Table */}
            {performanceSummary.employees?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 font-medium">Employee</th>
                      <th className="text-left p-2 font-medium">Region</th>
                      <th className="text-left p-2 font-medium">Divisions</th>
                      <th className="text-center p-2 font-medium">Customers</th>
                      <th className="text-right p-2 font-medium">Outstanding</th>
                      <th className="text-right p-2 font-medium">Overdue</th>
                      <th className="text-right p-2 font-medium">MTD Collection</th>
                      <th className="text-center p-2 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceSummary.employees.map((emp) => (
                      <tr key={emp.employee_id} className="border-t hover:bg-muted/30">
                        <td className="p-2">
                          <div className="font-medium">{emp.name}</div>
                          <div className="text-xs text-muted-foreground">{emp.email}</div>
                        </td>
                        <td className="p-2">{emp.region}</td>
                        <td className="p-2 text-xs max-w-[150px]">
                          {(emp.divisions || []).join(', ') || '-'}
                        </td>
                        <td className="p-2 text-center">{emp.customer_count}</td>
                        <td className="p-2 text-right font-medium">₹{(emp.total_outstanding / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-right text-red-600 font-medium">₹{(emp.total_overdue / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-right text-green-600 font-medium">₹{(emp.mtd_collected / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            emp.collection_rate >= 80 ? 'bg-green-100 text-green-800' :
                            emp.collection_rate >= 50 ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {emp.collection_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Head Office Row */}
                    {performanceSummary.head_office && (
                      <tr className="border-t-2 border-purple-200 bg-purple-50/50">
                        <td className="p-2">
                          <div className="font-medium text-purple-800">⭐ Head Office</div>
                          <div className="text-xs text-purple-600">Direct handling</div>
                        </td>
                        <td className="p-2 text-purple-700">{performanceSummary.head_office.region}</td>
                        <td className="p-2 text-xs text-purple-700">
                          {(performanceSummary.head_office.divisions || []).join(', ') || 'Direct'}
                        </td>
                        <td className="p-2 text-center text-purple-700">{performanceSummary.head_office.customer_count}</td>
                        <td className="p-2 text-right font-medium text-purple-700">₹{(performanceSummary.head_office.total_outstanding / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-right font-medium text-red-600">₹{(performanceSummary.head_office.total_overdue / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-right font-medium text-green-600">₹{(performanceSummary.head_office.mtd_collected / 100000).toFixed(1)}L</td>
                        <td className="p-2 text-center">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {performanceSummary.head_office.collection_rate}%
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Employee Management - Only show on employees tab */}
      {activeTab === 'employees' && (
      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-heading font-semibold flex items-center gap-2">
              <UserPlus className="text-blue-600" size={22} />
              Employee Management
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkAssign}
                className="text-xs"
              >
                <Building size={14} className="mr-1" />
                Auto-Assign by Region
              </Button>
              <Button
                onClick={() => openEmployeeDialog()}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus size={16} className="mr-1" />
                Add Employee
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Manage your team members. Assign them to regions and divisions. Customers can then be linked to employees for performance tracking.
          </p>
          
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : employees.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">No employees added yet. Click &quot;Add Employee&quot; to get started.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {employees.filter(e => e.is_active !== false).map((emp) => (
                <div key={emp.employee_id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 font-semibold">{emp.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{emp.name}</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_CONFIG[emp.role]?.color || 'bg-gray-100 text-gray-600'}`}>
                          {ROLE_CONFIG[emp.role]?.label || 'Sales Member'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{emp.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {(emp.regions || [emp.region]).filter(Boolean).join(', ') || 'No region'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(emp.divisions || [emp.division]).filter(Boolean).join(', ') || 'No division'}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEmployeeDialog(emp)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEmployee(emp)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
      
      {/* Send Smart Reminders - Only show on reminders tab */}
      {activeTab === 'reminders' && (
      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-heading font-semibold flex items-center gap-2">
            <Mail className="text-indigo-600" size={22} />
            Send Due Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Send overdue payment reminders based on customer-employee assignments:
          </p>
          <ul className="text-sm text-muted-foreground mb-4 list-disc list-inside space-y-1">
            <li><strong>Assigned customers:</strong> Reminder goes to the assigned employee</li>
            <li><strong>Head Office customers:</strong> Reminder goes to the business owner email below</li>
          </ul>
          
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <Label className="text-sm">Minimum Days Overdue</Label>
              <Input
                type="number"
                value={minDaysOverdue}
                onChange={(e) => setMinDaysOverdue(parseInt(e.target.value) || 1)}
                min={1}
                className="mt-1 w-32"
              />
            </div>
            <div className="flex-1 min-w-[250px]">
              <Label className="text-sm">Head Office Email (for unassigned customers)</Label>
              <Input
                type="email"
                value={headOfficeEmail}
                onChange={(e) => setHeadOfficeEmail(e.target.value)}
                placeholder="owner@company.com"
                className="mt-1"
              />
            </div>
            <Button
              onClick={handleSendSmartReminders}
              disabled={sendingReminders}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {sendingReminders ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Smart Reminders
                </>
              )}
            </Button>
          </div>
          
          {/* Reminder Result */}
          {reminderResult && (
            <div className={`mt-4 p-4 rounded-lg border ${reminderResult.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {reminderResult.status === 'error' ? (
                  <AlertCircle className="text-red-600" size={20} />
                ) : (
                  <CheckCircle className="text-emerald-600" size={20} />
                )}
                <span className={`font-semibold ${reminderResult.status === 'error' ? 'text-red-800' : 'text-emerald-800'}`}>
                  {reminderResult.message}
                </span>
              </div>
              
              {reminderResult.details && reminderResult.details.length > 0 && (
                <div className="mt-3 space-y-2">
                  {reminderResult.details.map((d, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded border text-sm ${d.is_head_office ? 'bg-purple-50 border-purple-200' : 'bg-white'}`}>
                      <div>
                        <span className="font-medium">{d.is_head_office ? '⭐ ' : ''}{d.recipient}</span>
                        <span className="text-muted-foreground ml-2">({d.email})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {d.status === 'sent' ? (
                          <>
                            <span className="text-muted-foreground">{d.customers} customers, {d.invoices} invoices</span>
                            <span className="font-semibold text-red-600">₹{d.total_overdue?.toLocaleString()}</span>
                            <CheckCircle className="text-emerald-600" size={16} />
                          </>
                        ) : (
                          <span className="text-red-600 text-xs">{d.error}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Format Locks Tab — admin only */}
      {activeTab === 'format_locks' && user?.role === 'admin' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <FileCheck className="text-blue-600" size={20} />
              Import Format Locks
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Upload one known-good sample file per import type. The system will extract its column headers and block any future upload that doesn't have every required column. Protects against corrupted files, wrong-type uploads, and silent template changes.
            </p>
          </CardHeader>
          <CardContent>
            {formatLocksLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
            ) : (
              <div className="space-y-2.5">
                {formatLocks.map(lock => (
                  <div key={lock.upload_type} className={`border rounded-lg p-3 ${lock.locked ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {lock.locked ? (
                          <Lock size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <Unlock size={14} className="text-amber-600 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">
                            {lock.label}
                            {lock.pdf_based && (
                              <span className="ml-1.5 text-[10px] font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded px-1.5 py-0.5 align-middle">PDF</span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {lock.locked ? (
                              lock.pdf_based ? (
                                <>
                                  Locked · {lock.marker_count} content markers · Ref: <span className="font-mono">{lock.reference_file_name}</span>
                                  {lock.reference_set_at && (
                                    <> · Set {new Date(lock.reference_set_at).toLocaleDateString('en-IN')} by {lock.reference_set_by}</>
                                  )}
                                </>
                              ) : (
                                <>
                                  Locked · {lock.column_count} required columns · Ref: <span className="font-mono">{lock.reference_file_name}</span>
                                  {lock.reference_set_at && (
                                    <> · Set {new Date(lock.reference_set_at).toLocaleDateString('en-IN')} by {lock.reference_set_by}</>
                                  )}
                                </>
                              )
                            ) : (
                              <span className="text-amber-700">
                                {lock.pdf_based
                                  ? 'No lock — PDF content checked against built-in markers only. Upload a reference PDF to lock.'
                                  : 'No lock — any file format will be accepted. Upload a reference to lock.'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {lock.locked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadReference(lock.upload_type, lock.reference_file_name)}
                            className="h-8 text-xs"
                            title="Download the stored reference sample"
                          >
                            <Download size={12} className="mr-1" /> Sample
                          </Button>
                        )}
                        <label className="inline-flex">
                          <input
                            type="file"
                            accept={lock.pdf_based ? '.pdf' : '.xlsx,.xlsm'}
                            className="hidden"
                            disabled={uploadingRef === lock.upload_type}
                            onChange={e => {
                              const f = e.target.files?.[0];
                              e.target.value = ''; // reset so re-picking the same file fires again
                              if (f) handleUploadReference(lock.upload_type, f);
                            }}
                          />
                          <span className={`inline-flex items-center gap-1 border rounded h-8 px-2.5 text-xs cursor-pointer ${uploadingRef === lock.upload_type ? 'bg-gray-100 text-gray-400' : lock.locked ? 'bg-white hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-700'}`}>
                            <Upload size={12} />
                            {uploadingRef === lock.upload_type ? 'Uploading…' : lock.locked ? 'Replace' : 'Set Reference'}
                          </span>
                        </label>
                        {lock.locked && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnlockFormat(lock.upload_type, lock.label)}
                            className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            title="Temporarily disable format validation for this upload type"
                          >
                            <Unlock size={12} className="mr-1" /> Unlock
                          </Button>
                        )}
                      </div>
                    </div>
                    {lock.locked && lock.pdf_based && lock.required_markers && lock.required_markers.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">Show content markers ({lock.required_markers.length})</summary>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {lock.required_markers.map((m, i) => (
                            <span key={i} className="text-[10px] bg-white border rounded px-1.5 py-0.5 font-mono">{m}</span>
                          ))}
                        </div>
                      </details>
                    )}
                    {lock.locked && !lock.pdf_based && lock.required_columns && lock.required_columns.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">Show required columns ({lock.required_columns.length})</summary>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {lock.required_columns.map((col, i) => (
                            <span key={i} className="text-[10px] bg-white border rounded px-1.5 py-0.5 font-mono">{col}</span>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Trade Fabric Category Rules ─────────────────────────── */}
      {activeTab === 'trade_rules' && (user?.role === 'admin' || user?.role === 'accounts') && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <FileCheck className="text-indigo-600" size={20} />
              Product Mix — Trade Fabric Rules
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Set the fabric category (Shirting / Suiting / Ethnic / Other) for each trade. One rule per (division, trade); rules beat every other classification signal. Unset trades fall back to the dispatch row's own category, then the order's category, then a default of Shirting.
            </p>
          </CardHeader>
          <CardContent>
            {/* Toolbar: scope + division + filters + maintenance */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Division (view)</label>
                <select
                  value={tradeRulesDivision}
                  onChange={(e) => setTradeRulesDivision(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  {DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Edit scope</label>
                <select
                  value={tradeRulesScope}
                  onChange={(e) => setTradeRulesScope(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white"
                  title="Global rules apply across every division. Per-division rules override globals for the selected division only."
                >
                  <option value="global">Global (all divisions)</option>
                  <option value="per_division">This division only</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Filter</label>
                <select
                  value={tradeRulesFilter}
                  onChange={(e) => setTradeRulesFilter(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  <option value="all">All trades</option>
                  <option value="unset">No rule</option>
                  <option value="set">Rule set</option>
                  <option value="default_fallback">Falling to default Shirting</option>
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Search</label>
                <Input
                  value={tradeRulesSearch}
                  onChange={(e) => setTradeRulesSearch(e.target.value)}
                  placeholder="e.g. carino, vyff, primante"
                  className="text-sm"
                />
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => fetchTradeRulesData(tradeRulesDivision)}
                disabled={tradeRulesLoading}
              >
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => openMergeDialog(mergePick)}
                disabled={mergePick.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                title="File the ticked names under one quality name, everywhere in the app"
              >
                Merge selected{mergePick.length ? ` (${mergePick.length})` : ''} into…
              </Button>
              {user?.role === 'admin' && (
                <>
                  <Button size="sm" variant="outline" onClick={runBackfillDispatchCategory}>
                    Backfill from orders
                  </Button>
                  <Button size="sm" variant="outline" onClick={runSeedTradeRules}>
                    Seed legacy rules
                  </Button>
                </>
              )}
            </div>

            {/* Coverage banner */}
            {!tradeRulesLoading && tradeRulesCoverage.length > 0 && (
              <div className="mb-3 text-xs text-slate-600">
                {(() => {
                  const set = tradeRulesCoverage.filter(t => t.current_rule_fabric).length;
                  const unset = tradeRulesCoverage.filter(t => !t.current_rule_fabric).length;
                  const unsetVal = tradeRulesCoverage
                    .filter(t => !t.current_rule_fabric)
                    .reduce((s, t) => s + (t.net_value || 0), 0);
                  return (
                    <span>
                      <strong>{tradeRulesDivision}</strong> · {tradeRulesCoverage.length} trades dispatched in last 90 days · {set} with rules · {unset} without {unset > 0 && <>(₹{(unsetVal / 100000).toFixed(2)}L recent dispatches)</>}
                    </span>
                  );
                })()}
              </div>
            )}

            {tradeRulesLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border p-2 w-8" title="Tick names to merge into one quality"></th>
                      <th className="border p-2 text-left font-medium">Trade</th>
                      <th className="border p-2 text-center font-medium">Effective Fabric</th>
                      <th className="border p-2 text-left font-medium">Source</th>
                      <th className="border p-2 text-center font-medium">Override</th>
                      <th className="border p-2 text-right font-medium" title="Net dispatched value in the last 90 days">90-day ₹</th>
                      <th className="border p-2 text-right font-medium" title="Trade's share of total dispatch value in the last 90 days">90d %</th>
                      <th className="border p-2 text-right font-medium" title="Net dispatched value in the last 365 days (rolling annual)">365-day ₹</th>
                      <th className="border p-2 text-right font-medium" title="Trade's share of total dispatch value in the last 365 days">365d %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Build a combined view from coverage rows (which
                      // already carry the effective_fabric resolved by
                      // the backend chain). Rules-only trades (no recent
                      // dispatch) are merged in so admin can still edit
                      // them.
                      const byTrade = new Map();
                      tradeRulesCoverage.forEach(t => {
                        byTrade.set(t.canonical_trade_name, {
                          trade: t.trade_name,
                          canonical: t.canonical_trade_name,
                          rule: t.current_rule_fabric || null,
                          effective: t.effective_fabric || null,
                          source: t.effective_source || null,
                          pieces: t.pieces || 0,
                          net_value: t.net_value || 0,
                          pct_value_90: t.pct_value_90 || 0,
                          net_value_365: t.net_value_365 || 0,
                          pct_value_365: t.pct_value_365 || 0,
                          merged_from: t.merged_from || [],
                        });
                      });
                      // Merge in trades that only exist as rules (no
                      // recent dispatch in this division). Global rules
                      // ('*') are SKIPPED here — they correctly affect
                      // any trade that does exist in this division
                      // (resolved via current_rule_fabric inside
                      // coverage), but should not inject phantom rows
                      // for trades that have no dispatch presence in
                      // the selected division. Only per-division rules
                      // are added as synthetic 'rule-only' rows.
                      tradeRulesList.forEach(r => {
                        if (r.division === '*') return;
                        const key = r.trade_name;
                        if (!byTrade.has(key)) {
                          byTrade.set(key, {
                            trade: r.trade_name,
                            canonical: r.trade_name,
                            rule: r.fabric,
                            effective: r.fabric,
                            source: 'rule',
                            pieces: 0, net_value: 0,
                            pct_value_90: 0, net_value_365: 0, pct_value_365: 0,
                          });
                        }
                      });
                      let rows = Array.from(byTrade.values());
                      if (tradeRulesFilter === 'unset') rows = rows.filter(r => !r.rule);
                      if (tradeRulesFilter === 'set') rows = rows.filter(r => r.rule);
                      if (tradeRulesFilter === 'default_fallback') rows = rows.filter(r => r.source === 'default');
                      if (tradeRulesSearch.trim()) {
                        const s = tradeRulesSearch.toLowerCase();
                        rows = rows.filter(r => (r.trade || '').toLowerCase().includes(s));
                      }
                      rows.sort((a, b) => b.net_value - a.net_value);
                      if (rows.length === 0) {
                        return (
                          <tr>
                            <td colSpan="9" className="border p-4 text-center text-muted-foreground">
                              No trades match the current filters.
                            </td>
                          </tr>
                        );
                      }
                      const fabricColour = {
                        Shirting: 'bg-sky-100 text-sky-800',
                        Suiting:  'bg-violet-100 text-violet-800',
                        Ethnic:   'bg-pink-100 text-pink-800',
                        Other:    'bg-slate-100 text-slate-700',
                        Unclear:  'bg-amber-100 text-amber-800',
                      };
                      const sourceLabel = {
                        rule: 'Manual rule',
                        'dispatch.category': 'From dispatch tag',
                        'order.category': 'From order copy',
                        occa_prefix: 'OCCA prefix',
                        default: 'Default (Shirting)',
                        // Mix / pack bales: Mix Bale Shirting → Shirting,
                        // Mix Bale Suiting → Suiting, the rest → Other.
                        mix_label: 'Mix / pack label',
                      };
                      return rows.map(r => (
                        <tr key={r.canonical} className={r.source === 'default' ? 'bg-amber-50/30' : ''}>
                          <td className="border p-2 text-center">
                            <input type="checkbox" checked={mergePick.includes(r.canonical)}
                              onChange={(e) => setMergePick(p => e.target.checked ? [...p, r.canonical] : p.filter(x => x !== r.canonical))} />
                          </td>
                          <td className="border p-2 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{r.trade}</span>
                              <button type="button" onClick={() => openMergeDialog([r.canonical])}
                                className="text-[11px] font-normal text-indigo-600 hover:underline"
                                title="Merge this name into another quality, or rename it">Merge / rename</button>
                            </div>
                            {r.merged_from?.length > 0 && (
                              <div className="text-[11px] font-normal text-slate-500 mt-0.5">also includes: {r.merged_from.join(', ')}</div>
                            )}
                          </td>
                          <td className="border p-2 text-center">
                            {r.effective ? (
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${fabricColour[r.effective] || ''}`}>
                                {r.effective}
                              </span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="border p-2 text-xs text-slate-600">
                            {sourceLabel[r.source] || r.source || <span className="text-slate-400">—</span>}
                          </td>
                          <td className="border p-2 text-center">
                            <select
                              value={r.rule || ''}
                              onChange={(e) => saveTradeRule(r.canonical, e.target.value || null)}
                              disabled={savingRuleTrade === r.canonical}
                              className={`border rounded px-2 py-1 text-xs ${r.rule ? 'bg-emerald-50 border-emerald-300' : 'bg-white'}`}
                              title={tradeRulesScope === 'global' ? 'Writes a global rule (all divisions)' : `Overrides global for ${tradeRulesDivision} only`}
                            >
                              <option value="">— Auto —</option>
                              <option value="Shirting">Shirting</option>
                              <option value="Suiting">Suiting</option>
                              <option value="Ethnic">Ethnic</option>
                              <option value="Other">Other (Combo/RTS)</option>
                              <option value="Unclear">Unclear/Unknown</option>
                            </select>
                          </td>
                          <td className="border p-2 text-right tabular-nums">{r.net_value > 0 ? `₹${(r.net_value / 100000).toFixed(2)}L` : '—'}</td>
                          <td className="border p-2 text-right tabular-nums text-slate-600">{r.net_value > 0 ? `${r.pct_value_90.toFixed(1)}%` : '—'}</td>
                          <td className="border p-2 text-right tabular-nums">{r.net_value_365 > 0 ? `₹${(r.net_value_365 / 100000).toFixed(2)}L` : '—'}</td>
                          <td className="border p-2 text-right tabular-nums text-slate-600">{r.net_value_365 > 0 ? `${r.pct_value_365.toFixed(1)}%` : '—'}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* Merged names — undo any of them */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-800">Merged names</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Names filed under another quality everywhere in the app — Orders &amp; Dispatch, Product Mix, reports, exports and these fabric rules.
                Uploaded lines keep their original name, so undoing a merge brings it straight back.
              </p>
              {tradeMerges.length === 0 ? (
                <p className="text-xs text-slate-500">None yet. Tick names above and use “Merge selected into…”, or “Merge / rename” on a row.</p>
              ) : (
                <table className="w-full border-collapse text-sm max-w-3xl">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border p-2 text-left font-medium">Name as uploaded</th>
                      <th className="border p-2 text-left font-medium">Shows as</th>
                      <th className="border p-2 text-left font-medium">Applies to</th>
                      <th className="border p-2 text-left font-medium">By</th>
                      <th className="border p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeMerges.map(m => (
                      <tr key={m.merge_id}>
                        <td className="border p-2">{m.variant}</td>
                        <td className="border p-2 font-semibold">{m.target}</td>
                        <td className="border p-2 text-xs">{m.division === '*' ? 'All divisions' : m.division}</td>
                        <td className="border p-2 text-xs text-slate-500">{(m.updated_by || m.created_by || '').split('@')[0]}</td>
                        <td className="border p-2 text-center">
                          <button type="button" onClick={() => undoMerge(m)} className="text-xs text-rose-600 hover:underline">Undo</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!mergeDialog} onOpenChange={(o) => { if (!o) setMergeDialog(null); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{mergeDialog?.variants.length > 1 ? `Merge ${mergeDialog.variants.length} names` : 'Merge or rename'}</DialogTitle>
          </DialogHeader>
          {mergeDialog && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Name{mergeDialog.variants.length > 1 ? 's' : ''}</div>
                <div className="flex flex-wrap gap-1">
                  {mergeDialog.variants.map(v => <span key={v} className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-xs font-medium">{v}</span>)}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Show as</label>
                <Input list="trade-merge-targets" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}
                  placeholder="Pick an existing quality or type a new name, e.g. Aspro" autoFocus />
                <datalist id="trade-merge-targets">
                  {tradeRulesCoverage.map(c => c.canonical_trade_name)
                    .filter(n => !mergeDialog.variants.includes(n))
                    .map(n => <option key={n} value={n} />)}
                </datalist>
                <p className="text-[11px] text-slate-500 mt-1">An existing quality merges the name{mergeDialog.variants.length > 1 ? 's' : ''} into it; a new name renames.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Applies to</label>
                <select value={mergeScope} onChange={(e) => setMergeScope(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white">
                  <option value="division">{tradeRulesDivision} only</option>
                  <option value="global">All divisions</option>
                </select>
              </div>
              <p className="text-[11px] text-slate-500">
                Takes effect everywhere at once — Orders &amp; Dispatch, Product Mix, reports and exports. Uploaded lines keep their original name; undo any time under “Merged names”.
                A fabric rule on a merged name moves to the target if the target has none.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog(null)}>Cancel</Button>
            <Button onClick={saveMerge} disabled={mergeSaving || !mergeTarget.trim()} className="bg-indigo-600 hover:bg-indigo-700">
              {mergeSaving ? 'Saving…' : 'Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Stores (ship-to) back-fill ──────────────────────────── */}
      {activeTab === 'trade_rules' && user?.role === 'admin' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <Building className="text-amber-600" size={20} />
              Stores (TAS) back-fill
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Re-reads the retained dispatch reports and order copies and stamps the <strong>Ship To Party</strong> (store) on rows that have none, seeding the store master under each SAP code. Billing is untouched — stores only split orders, dispatches and targets. Rows that already carry a store are skipped; where two files disagree the later upload wins.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Division</label>
                <select
                  value={shipToDivision}
                  onChange={(e) => setShipToDivision(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white"
                  title="Only TAS files carry a ship-to today"
                >
                  {DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <Button size="sm" variant="outline" onClick={() => runBackfillShipTo(true)} disabled={!!shipToRunning}>
                {shipToRunning === 'dry' ? 'Reading files…' : 'Dry run'}
              </Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => runBackfillShipTo(false)} disabled={!!shipToRunning}>
                {shipToRunning === 'apply' ? 'Applying…' : 'Apply'}
              </Button>
              <Button size="sm" variant="outline" className="text-sky-700 border-sky-300 hover:bg-sky-50" onClick={runMatchPreview} disabled={matchPreviewRunning}
                title="Simulates a Re-Run of reconciliation with store-aware matching on and off; writes nothing">
                {matchPreviewRunning ? 'Simulating…' : 'Preview store-aware matching'}
              </Button>
            </div>

            {matchPreview && (() => {
              const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
              const on = matchPreview.with_store_rule || {};
              const off = matchPreview.without_store_rule || {};
              const rows = [
                ['Order lines whose status or dispatched units change', 'order_lines_changing'],
                ['… of which back to pending', 'order_lines_to_pending'],
                ['… of which to partial', 'order_lines_to_partial'],
                ['… of which to fulfilled', 'order_lines_to_fulfilled'],
                ['Net change in units counted as dispatched against orders', 'units_dispatched_delta'],
                ['Dispatches filling an order placed for another store (flagged store_mismatch)', 'dispatches_cross_store_fill'],
                ['… pieces on those dispatches', 'pieces_cross_store_fill'],
                ['Dispatches moved to another order line', 'dispatches_moved_to_another_line'],
                ['Dispatches newly linked to an order', 'dispatches_newly_linked'],
                ['Dispatches whose stale link is cleared', 'dispatches_stale_link_cleared'],
              ];
              return (
                <div className="mt-3 space-y-3">
                  <div className="text-xs px-2.5 py-1.5 rounded border bg-sky-50 border-sky-200 text-sky-800">
                    <strong>Simulation only — nothing written.</strong> {matchPreview.division}: {n(matchPreview.orders_loaded)} order lines, {n(matchPreview.dispatches_loaded)} dispatches, {n(matchPreview.stores_in_master)} stores. "Without" = what Re-Run would do with the rule switched off (ordinary drift); "With" = with it on.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="text-xs border-collapse min-w-[520px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                          <th className="text-left pr-6 pb-1">What a Re-Run changes</th>
                          <th className="text-right pr-4 pb-1">Without rule</th>
                          <th className="text-right pb-1">With rule</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(([label, k]) => (
                          <tr key={k} className="border-b border-slate-100">
                            <td className="py-1 pr-6 text-slate-600">{label}</td>
                            <td className="py-1 pr-4 text-right tabular-nums">{n(off[k])}</td>
                            <td className={`py-1 text-right tabular-nums font-medium ${k === 'dispatches_cross_store_fill' && on[k] > 0 ? 'text-amber-700' : ''}`}>{n(on[k])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(on.by_account || []).length > 0 && (
                    <details className="text-xs" open>
                      <summary className="cursor-pointer text-slate-600">Per account, with the rule ({on.by_account.length})</summary>
                      <div className="overflow-x-auto mt-1.5">
                        <table className="text-xs border-collapse min-w-[560px]">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                              <th className="text-left pr-4 pb-1">Code</th><th className="text-left pr-4 pb-1">Customer</th>
                              <th className="text-right pr-4 pb-1">Lines changing</th><th className="text-right pr-4 pb-1">Units Δ</th>
                              <th className="text-right pr-4 pb-1">Store swaps</th><th className="text-right pr-4 pb-1">Pieces</th><th className="text-right pb-1">Moved</th>
                            </tr>
                          </thead>
                          <tbody>
                            {on.by_account.map(a => (
                              <tr key={a.customer_code} className="border-b border-slate-100">
                                <td className="py-0.5 pr-4 font-mono">{a.customer_code}</td><td className="py-0.5 pr-4">{a.customer}</td>
                                <td className="py-0.5 pr-4 text-right tabular-nums">{n(a.order_lines)}</td><td className="py-0.5 pr-4 text-right tabular-nums">{n(a.units_delta)}</td>
                                <td className="py-0.5 pr-4 text-right tabular-nums">{n(a.dispatches_cut)}</td><td className="py-0.5 pr-4 text-right tabular-nums">{n(a.pieces_cut)}</td><td className="py-0.5 text-right tabular-nums">{n(a.dispatches_moved)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                  {(on.sample_cut_dispatches || []).length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-600">Store swaps — shipped to one store, order placed for another (first {on.sample_cut_dispatches.length})</summary>
                      <div className="overflow-x-auto mt-1.5">
                        <table className="text-xs border-collapse min-w-[640px]">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                              <th className="text-left pr-3 pb-1">Invoice</th><th className="text-left pr-3 pb-1">Date</th><th className="text-left pr-3 pb-1">Customer</th>
                              <th className="text-left pr-3 pb-1">Shipped to</th><th className="text-left pr-3 pb-1">Sort</th><th className="text-right pr-3 pb-1">Pcs</th>
                              <th className="text-left pr-3 pb-1">Fills order</th><th className="text-left pb-1">That order's store</th>
                            </tr>
                          </thead>
                          <tbody>
                            {on.sample_cut_dispatches.map((r, i) => (
                              <tr key={`${r.invoice}-${i}`} className="border-b border-slate-100">
                                <td className="py-0.5 pr-3 font-mono">{r.invoice}</td><td className="py-0.5 pr-3">{r.date}</td><td className="py-0.5 pr-3">{r.customer}</td>
                                <td className="py-0.5 pr-3">{r.store}</td><td className="py-0.5 pr-3 font-mono">{r.sort}</td><td className="py-0.5 pr-3 text-right tabular-nums">{n(r.pieces)}</td>
                                <td className="py-0.5 pr-3 font-mono">{r.was_order || '—'}</td><td className="py-0.5">{r.was_order_store || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}

            {shipToResult && (() => {
              const r = shipToResult;
              const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
              // The server answers {totals, parts: {dispatches: {...}, orders: {...}}}.
              const parts = r.parts || {};
              const partNames = Object.keys(parts);
              const g = (k) => (r.totals && r.totals[k] != null ? r.totals[k] : null);
              const per = (k) => partNames.length > 1
                ? partNames.map(pn => `${pn}: ${n(parts[pn]?.[k])}`).join(' · ')
                : null;
              const rows = [
                ['Files read', g('files_read')],
                ['File rows', g('file_rows')],
                ['Rows matched', g('rows_matched')],
                [r.dry_run ? 'Would set' : 'Rows set', g('rows_set')],
                ['Already set (skipped)', g('rows_already_set')],
                ['Conflicts (last file wins)', g('conflicts')],
                ['Left without store after', per('rows_without_ship_to_after') ?? g('rows_without_ship_to_after')],
                [r.dry_run ? 'Stores to create' : 'Stores created', g('stores_created')],
                ['Stores updated', g('stores_updated')],
              ].filter(([, v]) => v != null);
              const perAccount = partNames.flatMap(pn => (parts[pn]?.accounts || []).map(a => ({ ...a, part: pn })));
              return (
                <div className="space-y-3">
                  <div className={`text-xs px-2.5 py-1.5 rounded border ${r.dry_run ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    <strong>{r.dry_run ? 'Dry run' : 'Applied'}</strong> · {r.division} · {r.partial ? 'dispatches done, reading orders…' : 'dispatches + orders'}{r.dry_run ? ' · nothing was written' : ''}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="text-xs border-collapse min-w-[320px]">
                      <tbody>
                        {rows.map(([k, v]) => (
                          <tr key={k} className="border-b border-slate-100">
                            <td className="py-1 pr-6 text-slate-600">{k}</td>
                            <td className="py-1 text-right tabular-nums font-medium">
                              {typeof v === 'string' ? v : typeof v === 'object' ? Object.entries(v).map(([kk, vv]) => `${kk}: ${n(vv)}`).join(' · ') : n(v)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {perAccount.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-600">Per account ({perAccount.length})</summary>
                      <div className="overflow-x-auto mt-1.5">
                        <table className="text-xs border-collapse min-w-[420px]">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                              <th className="text-left pr-4 pb-1">Code</th>
                              <th className="text-left pr-4 pb-1">File</th>
                              <th className="text-right pr-4 pb-1">Ship-tos</th>
                              <th className="text-right pr-4 pb-1">File rows</th>
                              <th className="text-right pr-4 pb-1">{r.dry_run ? 'Would set' : 'Set'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {perAccount.map((a, i) => (
                              <tr key={`${a.part}-${a.customer_code || i}`} className="border-b border-slate-100">
                                <td className="py-0.5 pr-4 font-mono">{a.customer_code}</td>
                                <td className="py-0.5 pr-4">{a.part}</td>
                                <td className="py-0.5 pr-4 text-right tabular-nums">{n(a.ship_tos)}</td>
                                <td className="py-0.5 pr-4 text-right tabular-nums">{n(a.file_rows)}</td>
                                <td className="py-0.5 pr-4 text-right tabular-nums">{n(a.rows_set)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ─── Untagged Labels (sort-label → trade_name) ───────────── */}
      {activeTab === 'untagged_labels' && (user?.role === 'admin' || user?.role === 'accounts') && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <FileCheck className="text-indigo-600" size={20} />
              Untagged Labels — Sort → Trade Name
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Some dispatch rows carry a descriptive label (e.g. <strong>MIX DISPATCH</strong>, <strong>COMBO DISPATCH</strong>, <strong>VYFF-TURBO</strong>, <strong>LINEN THAN</strong>) instead of a product code, so they show as "(Untagged)" in reports. Map each label to a trade name here, then click <strong>Apply to dispatches</strong>. Matching ignores case and extra spaces. Product-code sorts (e.g. ZEBE13375-12) are resolved automatically and don't appear below.
            </p>
          </CardHeader>
          <CardContent>
            {/* Toolbar */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Division</label>
                <select
                  value={labelRulesDivision}
                  onChange={(e) => setLabelRulesDivision(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  <option value="all">All divisions</option>
                  {DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => fetchLabelRulesData(labelRulesDivision)}
                disabled={labelRulesLoading}
              >
                Refresh
              </Button>
              {(user?.role === 'admin' || user?.role === 'accounts') && (
                <Button
                  size="sm"
                  onClick={applyLabelRules}
                  disabled={applyingLabelRules}
                >
                  {applyingLabelRules ? 'Applying…' : 'Apply to dispatches'}
                </Button>
              )}
            </div>

            {/* Shared autocomplete source for all trade-name inputs —
                steers entries toward existing names to avoid near-
                duplicate trades (e.g. "Mix Shirting Dispatch"). */}
            <datalist id="trade-name-suggestions">
              {tradeNameSuggestions.map((n) => <option key={n} value={n} />)}
            </datalist>

            {labelRulesLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
            ) : (
              <div className="space-y-6">
                {/* Unmapped labels */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">
                    Unmapped labels {unmappedLabels.length > 0 && <span className="text-slate-400 font-normal">({unmappedLabels.length})</span>}
                  </h3>
                  {unmappedLabels.length === 0 ? (
                    <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
                      No unmapped descriptive labels{labelRulesDivision !== 'all' ? ` in ${labelRulesDivision}` : ''}. Everything either has a trade name or a rule.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border p-2 text-left font-medium">Label</th>
                            <th className="border p-2 text-left font-medium">Division</th>
                            <th className="border p-2 text-right font-medium">Lines</th>
                            <th className="border p-2 text-right font-medium">Pieces</th>
                            <th className="border p-2 text-left font-medium">Sample customers</th>
                            <th className="border p-2 text-left font-medium">Trade name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unmappedLabels.map((u) => {
                            const key = `${u.division}|||${u.label}`;
                            const similar = findSimilarTradeName(labelDrafts[key], tradeNameSuggestions);
                            return (
                              <tr key={key}>
                                <td className="border p-2 font-mono text-xs">{u.label_display || u.label}</td>
                                <td className="border p-2 text-xs text-slate-600">{u.division || '—'}</td>
                                <td className="border p-2 text-right tabular-nums">{u.line_count}</td>
                                <td className="border p-2 text-right tabular-nums">{u.pieces}</td>
                                <td className="border p-2 text-xs text-slate-600">{(u.sample_customers || []).slice(0, 3).join(', ')}{(u.sample_customers || []).length > 3 ? '…' : ''}</td>
                                <td className="border p-2">
                                  <div className="flex items-center gap-2">
                                    <Input
                                      list="trade-name-suggestions"
                                      value={labelDrafts[key] || ''}
                                      onChange={(e) => setLabelDrafts(prev => ({ ...prev, [key]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Enter') saveLabelRule(u.division, u.label, labelDrafts[key]); }}
                                      placeholder="e.g. Mix Dispatch"
                                      className="text-sm h-8"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => saveLabelRule(u.division, u.label, labelDrafts[key])}
                                      disabled={savingLabel === key || !(labelDrafts[key] || '').trim()}
                                    >
                                      Map
                                    </Button>
                                  </div>
                                  {similar && (
                                    <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-2 flex-wrap">
                                      <span>Similar existing trade: <strong>{similar}</strong>.</span>
                                      <button
                                        type="button"
                                        className="underline font-medium hover:text-amber-900"
                                        onClick={() => setLabelDrafts(prev => ({ ...prev, [key]: similar }))}
                                      >
                                        Use it
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Existing rules */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">
                    Existing rules {labelRules.length > 0 && <span className="text-slate-400 font-normal">({labelRules.length})</span>}
                  </h3>
                  {labelRules.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No rules yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border p-2 text-left font-medium">Label</th>
                            <th className="border p-2 text-left font-medium">Division</th>
                            <th className="border p-2 text-left font-medium">Trade name</th>
                            <th className="border p-2 text-center font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {labelRules.map((r) => {
                            const editVal = ruleEdits[r.rule_id] ?? r.trade_name ?? '';
                            const changed = editVal.trim() !== (r.trade_name || '').trim();
                            const similar = findSimilarTradeName(editVal, tradeNameSuggestions);
                            return (
                              <tr key={r.rule_id}>
                                <td className="border p-2 font-mono text-xs">{r.sort_label_display || r.sort_label}</td>
                                <td className="border p-2 text-xs text-slate-600">{r.division === '*' ? 'Global' : r.division}</td>
                                <td className="border p-2">
                                  <Input
                                    list="trade-name-suggestions"
                                    value={editVal}
                                    onChange={(e) => setRuleEdits(prev => ({ ...prev, [r.rule_id]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && changed) saveRuleEdit(r); }}
                                    className="text-sm h-8"
                                  />
                                  {similar && (
                                    <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-2 flex-wrap">
                                      <span>Similar existing trade: <strong>{similar}</strong>.</span>
                                      <button
                                        type="button"
                                        className="underline font-medium hover:text-amber-900"
                                        onClick={() => setRuleEdits(prev => ({ ...prev, [r.rule_id]: similar }))}
                                      >
                                        Use it
                                      </button>
                                    </div>
                                  )}
                                </td>
                                <td className="border p-2 text-center whitespace-nowrap">
                                  <Button
                                    size="sm"
                                    onClick={() => saveRuleEdit(r)}
                                    disabled={savingLabel === r.rule_id || !changed || !editVal.trim()}
                                    className="mr-2"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    onClick={() => deleteLabelRule(r)}
                                    disabled={savingLabel === r.rule_id}
                                  >
                                    Delete
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <div>
              <Label>Name *</Label>
              <Input
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email * <span className="text-xs text-muted-foreground font-normal">(Google sign-in email)</span></Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                placeholder="employee@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                placeholder="Phone number"
                className="mt-1"
              />
            </div>
            <div>
              <Label>App Access Role *</Label>
              <Select
                value={employeeForm.role || 'sales_member'}
                onValueChange={(v) => setEmployeeForm({ ...employeeForm, role: v })}
              >
                <SelectTrigger className="mt-1" data-testid="employee-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <span className="text-red-700 font-medium">Administrator</span>
                    <span className="text-xs text-muted-foreground ml-2">- Full access</span>
                  </SelectItem>
                  <SelectItem value="accounts">
                    <span className="text-blue-700 font-medium">Accounts</span>
                    <span className="text-xs text-muted-foreground ml-2">- Record payments, edit data</span>
                  </SelectItem>
                  <SelectItem value="sales_member">
                    <span className="text-gray-700 font-medium">Sales Member</span>
                    <span className="text-xs text-muted-foreground ml-2">- View only</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Import Permissions - Only for sales_member */}
            {(employeeForm.role === 'sales_member') && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="can-import-orders"
                    checked={employeeForm.can_import_orders || false}
                    onChange={e => setEmployeeForm({ ...employeeForm, can_import_orders: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="can-import-orders" className="text-sm font-medium cursor-pointer">
                    Can Import Orders
                  </Label>
                </div>
                {employeeForm.can_import_orders && (
                  <div className="ml-7 space-y-2">
                    <Label className="text-xs text-muted-foreground">Import Scope</Label>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="importScope"
                          value="own"
                          checked={employeeForm.import_orders_scope === 'own'}
                          onChange={() => setEmployeeForm({ ...employeeForm, import_orders_scope: 'own' })}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">Own Divisions Only</span>
                        <span className="text-xs text-muted-foreground">- Can only import for their assigned divisions</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="importScope"
                          value="all"
                          checked={employeeForm.import_orders_scope === 'all'}
                          onChange={() => setEmployeeForm({ ...employeeForm, import_orders_scope: 'all' })}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">All Divisions</span>
                        <span className="text-xs text-muted-foreground">- Can import orders for any division</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Regions - Multiple Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Regions * <span className="text-xs text-muted-foreground font-normal">(select all that apply)</span></Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllRegions}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={clearAllRegions}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {REGIONS.map((r) => (
                  <label 
                    key={r} 
                    className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${
                      (employeeForm.regions || []).includes(r) 
                        ? 'bg-green-50 border-green-300' 
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(employeeForm.regions || []).includes(r)}
                      onChange={() => toggleRegion(r)}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium">{r}</span>
                  </label>
                ))}
              </div>
              {(employeeForm.regions || []).length === 0 && (
                <p className="text-xs text-red-500 mt-1">Please select at least one region</p>
              )}
            </div>
            
            {/* Divisions - Multiple Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Divisions * <span className="text-xs text-muted-foreground font-normal">(select all that apply)</span></Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllDivisions}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={clearAllDivisions}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {availableDivisions.map((d) => (
                  <label 
                    key={d} 
                    className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-colors ${
                      (employeeForm.divisions || []).includes(d) 
                        ? 'bg-blue-50 border-blue-300' 
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(employeeForm.divisions || []).includes(d)}
                      onChange={() => toggleDivision(d)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium">{d}</span>
                  </label>
                ))}
              </div>
              {(employeeForm.divisions || []).length === 0 && (
                <p className="text-xs text-red-500 mt-1">Please select at least one division</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployee} className="bg-blue-600 hover:bg-blue-700">
              {editingEmployee ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
